// ─── RankControl ────────────────────────────────────────────────────────────
// The ↑ ↓ buttons that move an item up or down in the customer's ranking,
// plus an optional "remove" button. Replaces two older files that did almost
// the same thing: Preference2.jsx (on photos) and PreferenceControl2.jsx
// (on Space Planning cards).
//
// Two looks:
//   variant="overlay" — dark glass buttons that sit on top of a photo. They
//                       appear when the card is hovered, or when a keyboard
//                       user tabs onto them.
//   variant="card"    — light outlined buttons for white cards.
//
// The remove button only appears when you pass `onRemove`.
//
// Props:
//   onMoveUp, onMoveDown — called when an arrow is pressed
//   onRemove             — optional; adds the remove (bin) button
//   isFirst, isLast      — greys out the arrow that can't move any further
//   cardHovered          — overlay only: true while the mouse is over the card
//   itemLabel            — optional name used by screen readers, e.g. "Oak vinyl"
//   variant              — 'overlay' (default) or 'card'

import { Box } from '@mui/material';
import KeyboardArrowUpIcon   from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteOutlineIcon     from '@mui/icons-material/DeleteOutline';
import { CHARCOAL, MUTED_FAINT, STONE50, GREEN } from '../../theme/tokens2';

// ─── Looks ──────────────────────────────────────────────────────────────────
const OVERLAY = {
  size: 24,
  base: {
    borderRadius: '2px',
    border: 'none',
    outline: '1px solid rgba(255,255,255,0.7)',
    outlineOffset: '-1px',
    color: 'rgba(255,255,255,0.92)',
  },
  // Background goes darker as the pointer gets closer to "firing".
  rest: (cardHovered) => ({
    background: cardHovered ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.55)',
    '&:hover': { background: 'rgba(0,0,0,0.9)' },
  }),
  removeRest: () => ({
    background: 'rgba(0,0,0,0.32)',
    '&:hover': { background: 'rgba(180,35,24,0.82)', outlineColor: 'transparent' },
  }),
  iconSize: '1rem',
  gap: '3px',
  disabledOpacity: 0.35,
};

const CARD = {
  size: 22,
  base: {
    borderRadius: '4px',
    border: '1px solid rgba(0,0,0,0.18)',
    background: '#fff',
    color: CHARCOAL,
    opacity: 0.75,
  },
  rest: () => ({
    '&:hover': { backgroundColor: STONE50, borderColor: 'rgba(0,0,0,0.32)', opacity: 1 },
  }),
  removeRest: () => ({
    '&:hover': { backgroundColor: 'rgba(180,35,24,0.08)', borderColor: 'rgba(180,35,24,0.4)', color: '#B42318', opacity: 1 },
  }),
  iconSize: '0.875rem',
  gap: '2px',
  disabledOpacity: 0.4,
};

function RankButton({ look, label, onClick, disabled, extraSx, children }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      sx={{
        width: look.size, height: look.size, p: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'default' : 'pointer',
        userSelect: 'none', WebkitTapHighlightColor: 'transparent',
        transition: 'background 0.15s ease, border-color 0.15s ease, outline-color 0.15s ease, opacity 0.15s ease',
        ...look.base,
        ...extraSx,
        ...(disabled && { opacity: look.disabledOpacity, color: look === CARD ? MUTED_FAINT : look.base.color, pointerEvents: 'none' }),
        '&:focus-visible': { outline: `2px solid ${GREEN}`, outlineOffset: '1px' },
      }}
    >
      {children}
    </Box>
  );
}

export default function RankControl({
  onMoveUp, onMoveDown, onRemove,
  isFirst, isLast,
  cardHovered = false,
  itemLabel,
  variant = 'overlay',
}) {
  const look = variant === 'card' ? CARD : OVERLAY;
  const isOverlay = variant === 'overlay';
  const iconSx = { fontSize: look.iconSize };
  const name = itemLabel ? ` ${itemLabel}` : '';

  return (
    <Box
      sx={{
        display: 'flex', flexDirection: 'column', gap: look.gap, flexShrink: 0,
        // Overlay buttons hide until the card is hovered — but they must also
        // show for keyboard users who tab onto them (focus-within).
        ...(isOverlay && {
          opacity: cardHovered ? 1 : 0,
          transition: 'opacity 0.2s ease',
          '&:focus-within': { opacity: 1 },
          // Touch screens have no hover, so always show the buttons there.
          '@media (hover: none)': { opacity: 1 },
        }),
      }}
      // Stop the click from also opening the card underneath (e.g. a lightbox).
      onClick={(e) => e.stopPropagation()}
    >
      <RankButton look={look} label={`Move${name} up`} onClick={onMoveUp} disabled={isFirst} extraSx={look.rest(cardHovered)}>
        <KeyboardArrowUpIcon sx={iconSx} />
      </RankButton>

      <RankButton look={look} label={`Move${name} down`} onClick={onMoveDown} disabled={isLast} extraSx={look.rest(cardHovered)}>
        <KeyboardArrowDownIcon sx={iconSx} />
      </RankButton>

      {onRemove && (
        <RankButton look={look} label={`Remove${name}`} onClick={onRemove} extraSx={look.removeRest(cardHovered)}>
          <DeleteOutlineIcon sx={iconSx} />
        </RankButton>
      )}
    </Box>
  );
}
